import api from './api'
import cache from './cache'

/**
 * Shipping Label Service
 * Handles all shipping label related API operations
 */
class ShippingLabelService {
  /**
   * Create shipping labels for client items
   * @param {number} clientId - The client ID
   * @param {Object} labelData - Shipping label data
   * @param {Array<number>} labelData.item_ids - Array of item IDs to create labels for
   * @param {string} labelData.shipping_method - Shipping method (e.g., "FedEx.FEDEX_GROUND")
   * @param {number} labelData.warehouse_id - Warehouse ID to ship from
   * @returns {Promise<Object>} Response with created labels data
   */
  async createShippingLabels (clientId, labelData) {
    try {
      const response = await api.post(`/api/stylist/clients/${clientId}/shipping-label`, labelData)
      return response.data
    } catch (error) {
      // Re-throw the error with additional context
      if (error.response) {
        const errorData = error.response.data
        const errorMessage = errorData.message || errorData.error || 'Failed to create shipping labels'

        // Create a more descriptive error
        const enhancedError = new Error(errorMessage)
        enhancedError.status = error.response.status
        enhancedError.response = error.response
        enhancedError.errors = errorData.errors || {}

        throw enhancedError
      }
      throw error
    }
  }

  /**
   * Validate shipping label request data
   * @param {Object} labelData - The shipping label data to validate
   * @returns {Object} Validation result with isValid boolean and errors array
   */
  validateLabelData (labelData) {
    const errors = []

    if (!labelData.item_ids || !Array.isArray(labelData.item_ids) || labelData.item_ids.length === 0) {
      errors.push('Item IDs are required and must be a non-empty array')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  /**
   * Clear cache for warehouses and shipping methods
   * Useful for forcing a refresh of the data
   */
  clearCache () {
    cache.clear()
  }

  /**
   * Force refresh warehouses data (bypass cache)
   * @returns {Promise<Array>} Array of warehouse objects
   */
  async refreshWarehouses () {
    cache.clear()
    return this.getWarehouses()
  }

  /**
   * Force refresh shipping methods data (bypass cache)
   * @returns {Promise<Array>} Array of shipping method objects
   */
  async refreshShippingMethods () {
    cache.clear()
    return this.getShippingMethods()
  }

  /**
   * Get cache statistics for debugging
   * @returns {Object} Cache statistics
   */
  getCacheStats () {
    return cache.getStats()
  }
}

export default new ShippingLabelService()
